/**
 * Simple Google Maps overlay that renders the given content at the given
 * position, very similar to the built-in info window.
 *
 * The reason we use this overlay instead of the built-in info window is that
 * it's very limited and provides virtually no styling options. This overlay
 * allow us to fully utilize HTML, CSS and React to create interactive info
 * windows.
 *
 * Because of the nature of the Google Maps API (which is loaded dynamically
 * at runtime instead of being bundled in our app), the definition of the
 * class is hidden behind this function, since the class it extends from might
 * not be available in time if we defined the class at the top level.
 */
export const makePopupOverlayClass = () => {
  class PopupOverlay extends google.maps.OverlayView implements PopupOverlayMethods {
    position?: google.maps.LatLng | google.maps.LatLngLiteral | null
    container: HTMLElement
    content: HTMLElement
    anchor: HTMLElement
    popup: HTMLElement

    constructor(content: HTMLElement, position?: google.maps.LatLng | google.maps.LatLngLiteral | null) {
      super()
      this.position = position
      this.content = content

      this.popup = document.createElement('div')
      this.popup.classList.add('absolute', 'top-0', 'left-0', 'transform', '-translate-x-[50%]', '-translate-y-[100%]')
      this.popup.appendChild(content)

      this.anchor = document.createElement('div')
      this.anchor.classList.add('absolute', 'w-full', 'bottom-12', 'left-0')
      this.anchor.appendChild(this.popup)

      this.container = document.createElement('div')
      this.container.classList.add('absolute', 'h-0', 'w-min', 'cursor-auto', 'z-[1]')
      this.container.appendChild(this.anchor)

      PopupOverlay.preventMapHitsAndGesturesFrom(this.container)
    }

    onAdd() {
      this.getPanes()?.floatPane.appendChild(this.container)
    }

    onRemove() {
      if (this.container.parentElement) {
        this.container.parentElement.removeChild(this.container)
      }
    }

    draw() {
      if (!this.position) {
        return
      }

      const position = this.getProjection().fromLatLngToDivPixel(this.position)
      if (!position) {
        return
      }

      const display = Math.abs(position.x) < 4000 && Math.abs(position.y) < 4000 ? 'block' : 'none'

      if (display === 'block') {
        this.container.style.left = position.x + 'px'
        this.container.style.top = position.y + 'px'
      }

      if (this.container.style.display !== display) {
        this.container.style.display = display
      }
    }

    setPosition(position: google.maps.LatLng | google.maps.LatLngLiteral) {
      this.position = position
      this.draw()
    }

    panToPopup(): void {
      if (!this.position) {
        return
      }

      const position = this.getProjection().fromLatLngToContainerPixel(this.position)
      if (!position) {
        return
      }

      const popupRect = this.popup.getBoundingClientRect()
      const containerRect = this.container.getBoundingClientRect()
      const projection = this.getProjection()
      const popupCenter = projection.fromContainerPixelToLatLng(
        new google.maps.Point(position.x, position.y - (containerRect.bottom - popupRect.top) / 2)
      )

      const map = this.getMap() as google.maps.Map | undefined
      if (map && popupCenter) {
        map.panTo(popupCenter)
      }
    }
  }

  return PopupOverlay
}

export interface PopupOverlayMethods {
  setPosition(position: google.maps.LatLng | google.maps.LatLngLiteral): void
  panToPopup(): void
}
